import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useToast } from '../components/Toast'

interface OrcamentoPublic {
  id: number
  numero: string
  valor: number
  descricao?: string
  data_criacao: string
  ordem_servico: {
    numero: string
    cliente: {
      nome: string
      email?: string
      telefone?: string
    }
    equipamento?: {
      modelo: {
        nome: string
        marca: {
          nome: string
        }
      }
      numero_serie?: string
    }
  }
  itens: Array<{
    descricao: string
    quantidade: number
    preco_unitario: number
    total: number
  }>
}

const PublicOrcamentoPage: React.FC = () => {
  const { id, token } = useParams<{ id: string; token: string }>()
  const navigate = useNavigate()
  const { success, error } = useToast()
  const [orcamento, setOrcamento] = useState<OrcamentoPublic | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchOrcamento = async () => {
      try {
        const response = await fetch(`/api/public/orcamento/${id}/${token}`)

        if (!response.ok) {
          throw new Error('Orçamento não encontrado ou token inválido')
        }

        const data = await response.json()
        setOrcamento(data.data)
      } catch (err: any) {
        error(err.message || 'Erro ao carregar orçamento')
      } finally {
        setLoading(false)
      }
    }

    if (id && token) {
      fetchOrcamento()
    }
  }, [id, token])

  const handleApprove = async () => {
    if (!id || !token) return

    setSubmitting(true)
    try {
      const response = await fetch(`/api/public/orcamento/${id}/${token}/approve`, {
        method: 'POST',
      })

      if (!response.ok) {
        throw new Error('Erro ao aprovar orçamento')
      }

      success('Orçamento aprovado com sucesso! Entraremos em contato em breve.')
      setTimeout(() => {
        // Redirecionar para uma página de confirmação ou fechar
        window.close()
      }, 2000)
    } catch (err: any) {
      error(err.message || 'Erro ao aprovar orçamento')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!id || !token) return

    const motivo = prompt('Por favor, informe o motivo da rejeição (opcional):')

    setSubmitting(true)
    try {
      const response = await fetch(`/api/public/orcamento/${id}/${token}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ motivo }),
      })

      if (!response.ok) {
        throw new Error('Erro ao rejeitar orçamento')
      }

      success('Orçamento rejeitado. Obrigado pelo retorno!')
      setTimeout(() => {
        window.close()
      }, 2000)
    } catch (err: any) {
      error(err.message || 'Erro ao rejeitar orçamento')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando orçamento...</p>
        </div>
      </div>
    )
  }

  if (!orcamento) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Orçamento não encontrado</h1>
          <p className="text-gray-600">O orçamento solicitado não foi encontrado ou o link está inválido.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Cabeçalho */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">NexaMakers</h1>
              <p className="text-gray-600">Orçamento #{orcamento.numero}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Data</p>
              <p className="font-semibold">{new Date(orcamento.data_criacao).toLocaleDateString('pt-PT')}</p>
            </div>
          </div>

          {/* Informações do Cliente */}
          <div className="border-t pt-4">
            <h2 className="font-semibold text-gray-700 mb-2">Cliente</h2>
            <p className="text-gray-800 font-medium">{orcamento.ordem_servico.cliente.nome}</p>
            {orcamento.ordem_servico.cliente.email && (
              <p className="text-gray-600 text-sm">📧 {orcamento.ordem_servico.cliente.email}</p>
            )}
            {orcamento.ordem_servico.cliente.telefone && (
              <p className="text-gray-600 text-sm">📱 {orcamento.ordem_servico.cliente.telefone}</p>
            )}
          </div>

          {/* Informações do Equipamento */}
          {orcamento.ordem_servico.equipamento && (
            <div className="border-t mt-4 pt-4">
              <h2 className="font-semibold text-gray-700 mb-2">Equipamento</h2>
              <p className="text-gray-800">
                {orcamento.ordem_servico.equipamento.modelo.marca.nome} {orcamento.ordem_servico.equipamento.modelo.nome}
              </p>
              {orcamento.ordem_servico.equipamento.numero_serie && (
                <p className="text-gray-600 text-sm">S/N: {orcamento.ordem_servico.equipamento.numero_serie}</p>
              )}
            </div>
          )}

          {/* Ordem de Serviço */}
          <div className="border-t mt-4 pt-4">
            <p className="text-gray-600 text-sm">Ordem de Serviço: <span className="font-semibold">{orcamento.ordem_servico.numero}</span></p>
          </div>
        </div>

        {/* Itens do Orçamento */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Itens do Orçamento</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Descrição</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Qtd</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Preço Unit.</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {orcamento.itens.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{item.descricao}</td>
                    <td className="py-3 px-4 text-center">{item.quantidade}</td>
                    <td className="py-3 px-4 text-right">{item.preco_unitario.toFixed(2)} €</td>
                    <td className="py-3 px-4 text-right font-semibold">{item.total.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2">
                  <td colSpan={3} className="py-4 px-4 text-right font-bold text-gray-800">Total:</td>
                  <td className="py-4 px-4 text-right font-bold text-xl text-blue-600">{orcamento.valor.toFixed(2)} €</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {orcamento.descricao && (
            <div className="mt-4 p-4 bg-gray-50 rounded">
              <p className="text-sm font-semibold text-gray-700 mb-1">Observações:</p>
              <p className="text-gray-600">{orcamento.descricao}</p>
            </div>
          )}
        </div>

        {/* Botões de Ação */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Decisão do Orçamento</h2>
          <p className="text-gray-600 mb-6">
            Por favor, revise o orçamento acima e indique se aprova ou rejeita a proposta.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleApprove}
              disabled={submitting}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processando...
                </>
              ) : (
                <>
                  ✓ Aprovar Orçamento
                </>
              )}
            </button>

            <button
              onClick={handleReject}
              disabled={submitting}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processando...
                </>
              ) : (
                <>
                  ✗ Rejeitar Orçamento
                </>
              )}
            </button>
          </div>

          <p className="text-sm text-gray-500 mt-4 text-center">
            Ao clicar em aprovar ou rejeitar, sua decisão será registrada e a equipe será notificada.
          </p>
        </div>

        {/* Rodapé */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>© {new Date().getFullYear()} NexaMakers - Todos os direitos reservados</p>
        </div>
      </div>
    </div>
  )
}

export default PublicOrcamentoPage
